import { createAction } from '@reduxjs/toolkit';
import type { AxiosResponse } from 'axios';

import { errorPayloadCreator, getPayloadCreator, type Meta } from '../core/actionHelpers';
import type { BaseResource, QcsSyncResourcesPayload } from '../core/resourceStoreTypes';
import type { TransactionProps } from '../transactions/transactionsTypes';
import type { ScheduledTransaction, ScheduledTransactionType } from './scheduledTransactionsTypes';

// `sourceBusinessId` must forward `''` / `'0'` verbatim — those are explicit personal sentinels
// (see `transactionsUtils.filterObject.businesses`). A `|| undefined` collapse would drop the
// override and let a Bill Scout-templated businessId win on the new series.
export type CreateBillScoutScheduleMetaProps = {
  sourceIsExcludedFromF2S?: boolean;
  sourceIsExcludedFromReports?: boolean;
  sourceBusinessId?: string;
  isSubscription?: boolean;
  /** Forwarded to the create-recurring-transaction request body as-is. */
  isUserVerified?: boolean;
};

export type CreateBillScoutScheduleMeta = {
  props?: CreateBillScoutScheduleMetaProps;
};

export type CreateBillScoutScheduleRequest = {
  clientId: string;
  schedule?: {
    isUserVerified: boolean;
  };
};

export type CreateBillScoutSchedulePayload = CreateBillScoutScheduleRequest & {
  id: string;
};

/** What QCS answers a Bill Scout create with: the series it created. */
export type CreateBillScoutScheduleResponse = {
  schedule?: Omit<ScheduledTransactionType, 'transaction'> & {
    source?: TransactionProps['source'];
    transaction?: TransactionProps | null;
  };
};
type CreateBillScoutScheduleSuccessMeta = Meta & { response?: AxiosResponse<CreateBillScoutScheduleResponse> };

const syncSchTxnPayloadCreator = getPayloadCreator<QcsSyncResourcesPayload<ScheduledTransactionType>>();
const schTxnPayloadCreator = getPayloadCreator<ScheduledTransaction>();
const schTxnsPayloadCreator = getPayloadCreator<ScheduledTransaction[]>();
const billScoutPayloadCreator = getPayloadCreator<CreateBillScoutSchedulePayload>();
const billScoutSuccessPayloadCreator = getPayloadCreator<
  CreateBillScoutSchedulePayload,
  BaseResource,
  CreateBillScoutScheduleSuccessMeta
>();

export const getScheduledTransactions = createAction('GET_SCHEDULED_TRANSACTIONS', getPayloadCreator());
export const getScheduledTransactionsSuccess = createAction(
  'GET_SCHEDULED_TRANSACTIONS_SUCCESS',
  syncSchTxnPayloadCreator,
);
export const getScheduledTransactionsFailure = createAction('GET_SCHEDULED_TRANSACTIONS_FAILURE', errorPayloadCreator);

export const createScheduledTransaction = createAction('CREATE_SCHEDULED_TRANSACTION', schTxnPayloadCreator);
export const createScheduledTransactionSuccess = createAction(
  'CREATE_SCHEDULED_TRANSACTION_SUCCESS',
  schTxnPayloadCreator,
);
export const createScheduledTransactionFailure = createAction(
  'CREATE_SCHEDULED_TRANSACTION_FAILURE',
  errorPayloadCreator,
);

export const updateScheduledTransaction = createAction('UPDATE_SCHEDULED_TRANSACTION', schTxnPayloadCreator);
export const updateScheduledTransactionSuccess = createAction(
  'UPDATE_SCHEDULED_TRANSACTION_SUCCESS',
  schTxnPayloadCreator,
);
export const updateScheduledTransactionFailure = createAction(
  'UPDATE_SCHEDULED_TRANSACTION_FAILURE',
  errorPayloadCreator,
);

export const deleteScheduledTransaction = createAction('DELETE_SCHEDULED_TRANSACTION', schTxnPayloadCreator);
export const deleteScheduledTransactionSuccess = createAction(
  'DELETE_SCHEDULED_TRANSACTION_SUCCESS',
  schTxnPayloadCreator,
);
export const deleteScheduledTransactionFailure = createAction(
  'DELETE_SCHEDULED_TRANSACTION_FAILURE',
  errorPayloadCreator,
);

export const batchScheduledTransactions = createAction('BATCH_SCHEDULED_TRANSACTIONS', schTxnsPayloadCreator);
export const batchScheduledTransactionsSuccess = createAction(
  'BATCH_SCHEDULED_TRANSACTIONS_SUCCESS',
  schTxnsPayloadCreator,
);
export const batchScheduledTransactionsFailure = createAction(
  'BATCH_SCHEDULED_TRANSACTIONS_FAILURE',
  errorPayloadCreator,
);

export const createBillScoutSchedule = createAction('CREATE_BILLSCOUT_SCHEDULE', billScoutPayloadCreator);
export const createBillScoutScheduleSuccess = createAction(
  'CREATE_BILLSCOUT_SCHEDULE_SUCCESS',
  billScoutSuccessPayloadCreator,
);
export const createBillScoutScheduleFailure = createAction('CREATE_BILLSCOUT_SCHEDULE_FAILURE', errorPayloadCreator);
